import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Icon } from '@iconify/react'
import { message } from 'antd'

import { fetchProjectWidgetCode, projectsState } from '../projects.slice'
import { showActionNotification } from '../../../components/notification'
import styles from './CopyWidgetLinkButton.module.css'

const copyTextToClipboard = async (text) => {
    if (!text) return false
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text)
            return true
        }
    } catch {
        // fall through
    }
    try {
        const el = document.createElement('textarea')
        el.value = String(text)
        el.setAttribute('readonly', '')
        el.style.position = 'fixed'
        el.style.left = '-9999px'
        document.body.appendChild(el)
        el.select()
        const ok = document.execCommand('copy')
        document.body.removeChild(el)
        return ok
    } catch {
        return false
    }
}

const CopyWidgetLinkButton = ({ projectId, className = '' }) => {
    const dispatch = useDispatch()
    const { projectWidgetCodeStatus } = useSelector(projectsState)

    const handleCopyWidgetLink = useCallback(
        async (event) => {
            event.preventDefault()
            event.stopPropagation()

            if (!projectId) {
                message.warning('Project is not ready yet')
                return
            }

            const response = await dispatch(
                fetchProjectWidgetCode({ id: projectId })
            )

            if (!fetchProjectWidgetCode.fulfilled.match(response)) {
                showActionNotification({
                    message: 'Failed to fetch widget link',
                    notificationKey: 'widget-link-fetch-failed',
                })
                return
            }

            const payload = response.payload
            if (!payload?.success) {
                showActionNotification({
                    message: payload?.message || 'Failed to fetch widget link',
                    notificationKey: 'widget-link-fetch-failed',
                })
                return
            }

            const link = payload?.result?.widgetLink
            if (!link) {
                message.warning('Widget link is not available for this project')
                return
            }

            try {
                const ok = await copyTextToClipboard(link)
                if (!ok) throw new Error('copy failed')
                showActionNotification({
                    message: 'Widget link copied',
                    subMessage: link,
                    notificationKey: 'widget-link-copied',
                })
            } catch {
                showActionNotification({
                    message: 'Failed to copy widget link',
                    subMessage: link,
                    notificationKey: 'widget-link-copy-failed',
                })
            }
        },
        [dispatch, projectId]
    )

    const isLoading = projectWidgetCodeStatus === 'loading'

    return (
        <button
            type="button"
            className={`${styles.widgetLinkTabBtn} ${
                isLoading ? styles.widgetLinkTabBtnExpanded : ''
            } ${className}`.trim()}
            onClick={handleCopyWidgetLink}
            onMouseDown={(event) => event.stopPropagation()}
            disabled={isLoading}
            aria-label="Copy widget link"
            title={isLoading ? 'Copying…' : 'Copy widget link'}
        >
            <Icon
                icon="mdi:link-variant"
                width={14}
                height={14}
                className={styles.widgetLinkTabIcon}
            />
            <span className={styles.widgetLinkTabText} aria-hidden="true">
                {isLoading ? 'Copying…' : 'Copy widget link'}
            </span>
        </button>
    )
}

export default CopyWidgetLinkButton
